/**
 * silenceThreeWarnings — suppress upstream-only three.js deprecation
 * notices that this library does not (and cannot) act on.
 *
 * Currently filtered:
 *
 *   `THREE.Clock: This module has been deprecated. Please use
 *    THREE.Timer instead.`
 *
 *   Emitted by `@react-three/fiber@9.x`'s root-store factory
 *   (`new THREE.Clock()` inside its internal events-*.esm.js). three.js
 *   deprecated `Clock` in r183; functional behaviour is unchanged.
 *   We do not use `THREE.Clock` ourselves — the only constructor that
 *   triggers the warning lives inside r3f. Once r3f migrates its root
 *   store to `THREE.Timer`, the filter pattern can be removed.
 *
 * Implementation: three.js's own warn/error/log helpers route through
 * `setConsoleFunction` when set, so we install a custom handler that
 * drops the listed messages and forwards everything else to the
 * previously-registered handler (or the native console as a
 * fallback). Idempotent — calling more than once is a no-op.
 */

import { getConsoleFunction, setConsoleFunction } from 'three';

type ConsoleType = 'log' | 'warn' | 'error';
type ConsoleFn = (type: ConsoleType, message: unknown, ...params: unknown[]) => void;
type MarkedFn = ConsoleFn & { [SILENCER_MARKER]?: true };

const SILENCER_MARKER = Symbol.for('beckhoff-xts-viewer-3d/silencer');

const SILENCED_PREFIXES: ReadonlyArray<{ type: ConsoleType; prefix: string }> = [
  { type: 'warn', prefix: 'THREE.Clock: This module has been deprecated.' },
];

export function silenceUpstreamThreeWarnings(): void {
  const existing = getConsoleFunction() as MarkedFn | null;
  // If the currently-installed console function is already ours, bail.
  // Re-installing would chain a redundant wrapper on every render.
  if (existing && existing[SILENCER_MARKER]) return;

  const filter: MarkedFn = ((type, message, ...params) => {
    if (
      typeof message === 'string' &&
      SILENCED_PREFIXES.some(
        (p) => p.type === type && (message as string).startsWith(p.prefix),
      )
    ) {
      return;
    }
    if (existing) {
      existing(type, message, ...params);
      return;
    }
    // Mirror the native fan-out three.js does when no custom function
    // is set — keep log / warn / error on their respective channels.
    const fn = (console as unknown as Record<ConsoleType, ConsoleFn['call']>)[
      type
    ];
    if (typeof fn === 'function') {
      (fn as (...a: unknown[]) => void).call(console, message, ...params);
    } else {
      console.log(message, ...params);
    }
  }) as MarkedFn;
  filter[SILENCER_MARKER] = true;

  setConsoleFunction(filter as ConsoleFn);
}
